import React, { useState, useEffect, useRef } from 'react';
import { Card, TextContainer, TextField, Button, Spinner, BlockStack, InlineStack, Banner, Tooltip } from '@shopify/polaris';
import SubscriptionModal from './SubscriptionModal';
import './Chat.css';
import './HistoryTooltip.css';

function getShopDomain() {
  const params = new URLSearchParams(window.location.search);
  return params.get('shop');
}
const SHOP_DOMAIN = getShopDomain() || null;
const INSTALL_ID = SHOP_DOMAIN;
const API_BASE = process.env.REACT_APP_API_BASE || '';

const EXAMPLE_QUERIES = [
  "\"List top 3 sales\"",
  "\"List top 50 customers\"",
  "\"List of top 3 products per margin\""
];

// Helper to map Shopify GraphQL errors to user-friendly messages
function getFriendlyShopifyError(error) {
  if (!error) return null;
  if (typeof error === 'string') {
    if (/doesn't exist on type/i.test(error)) return "This field doesn't exist in your Shopify store. Please check your query.";
    if (/permission/i.test(error)) return "You do not have permission to access this data. Please check your app permissions.";
    if (/access denied|unauthorized/i.test(error)) return "Your Shopify session has expired. Please reconnect your store.";
    return error;
  }
  if (error.message) {
    if (/doesn't exist on type/i.test(error.message)) return "This field doesn't exist in your Shopify store. Please check your query.";
    if (/permission/i.test(error.message)) return "You do not have permission to access this data. Please check your app permissions.";
    if (/access denied|unauthorized/i.test(error.message)) return "Your Shopify session has expired. Please reconnect your store.";
    return error.message;
  }
  return JSON.stringify(error);
}

// Add a helper to check for a valid shop domain
function requireShopDomain() {
  if (!SHOP_DOMAIN) {
    return 'No shop detected in the URL. Please access the app from your Shopify admin or with ?shop=your-shop.myshopify.com in the URL.';
  }
  return null;
}

// Add a mapping from technical field names to user-friendly labels
const FIELD_LABELS = {
  'currentTotalPriceSet.shopMoney.amount': 'Order Value',
  'subtotalPriceSet.shopMoney.amount': 'Subtotal',
  'totalShippingPriceSet.shopMoney.amount': 'Shipping Amount',
  'totalTaxSet.shopMoney.amount': 'Tax Amount',
  'totalSpent.amount': 'Total Spent',
  'totalInventory': 'Total Inventory',
  'quantity': 'Quantity',
  'priceRange.minVariantPrice.amount': 'Min Variant Price',
  'priceRange.maxVariantPrice.amount': 'Max Variant Price',
  'orders': 'Orders',
  'products': 'Products',
  'customers': 'Customers',
  'id': 'ID',
  'name': 'Name',
  'email': 'Email',
  'title': 'Title',
  'status': 'Status',
  'createdAt': 'Created At',
  'firstName': 'First Name',
  'lastName': 'Last Name',
  // Add more as needed
};

function prettifyFieldName(field) {
  if (!field) return 'Result';
  if (FIELD_LABELS[field]) return FIELD_LABELS[field];
  // Fallback: prettify dot notation
  return field.split('.').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

// Add a mapping for user-friendly operation names
const OP_LABELS = {
  sum: 'Sum',
  average: 'Average',
  avg: 'Average',
  count: 'Count',
  min: 'Minimum',
  max: 'Maximum',
};

function renderAggregationResult(result, aggregation) {
  // Diagnostic logging
  console.log('[renderAggregationResult] aggregation_result:', result, 'typeof:', typeof result, 'isArray:', Array.isArray(result));
  if (Array.isArray(result)) {
    result.forEach((row, i) => {
      console.log(`[renderAggregationResult] Row ${i}:`, row, 'typeof:', typeof row, 'isArray:', Array.isArray(row));
    });
    if (result.length === 0) return <div>No results found.</div>;
    // If not all rows are plain objects, show error
    if (!result.every(row => typeof row === 'object' && row !== null && !Array.isArray(row))) {
      return <div className="aggregation-result error">Invalid result format: array contains non-object or array values.</div>;
    }
    // Collect all unique keys from all items for table headers
    const allKeys = Array.from(new Set(result.flatMap(item => Object.keys(item))));
    return (
      <div className="aggregation-result">
        <table className="aggregation-table">
          <thead>
            <tr>
              {allKeys.map(key => <th key={key}>{prettifyFieldName(key)}</th>)}
            </tr>
          </thead>
          <tbody>
            {result.map((item, idx) => {
              if (typeof item !== 'object' || item === null || Array.isArray(item)) {
                console.error('[renderAggregationResult] Non-object row:', item);
                return (
                  <tr key={idx}>
                    <td colSpan={allKeys.length} style={{ color: 'red', background: '#ffeaea' }}>
                      Invalid row: {JSON.stringify(item)}
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={item.id || idx}>
                  {allKeys.map(key => <td key={key}>{renderCell(item[key])}</td>)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }
  // Always round numeric results to 3 decimal places for display
  let displayResult = result;
  if (
    (typeof result === 'number' && isFinite(result)) ||
    (!isNaN(result) && result !== null && result !== undefined && result !== '')
  ) {
    const num = Number(result);
    if (!isNaN(num) && isFinite(num)) displayResult = num.toFixed(3);
  }
  console.log('displayResult:', displayResult, 'raw:', result);
  if (aggregation && aggregation.operation) {
    const opKey = aggregation.operation.toLowerCase();
    const fieldLabel = aggregation.field ? prettifyFieldName(aggregation.field) : '';
    if (opKey === 'count') {
      let entity = 'items';
      if (/order/i.test(fieldLabel)) entity = 'orders';
      else if (/customer/i.test(fieldLabel)) entity = 'customers';
      else if (/product/i.test(fieldLabel)) entity = 'products';
      else if (/id/i.test(fieldLabel)) entity = 'items';
      return (
        <div className="aggregation-result">
          <div className="aggregation-value">You have {`${displayResult}`} {entity}.</div>
        </div>
      );
    }
    const opMap = {
      sum: 'The total',
      average: 'The average',
      min: 'The minimum',
      max: 'The maximum'
    };
    if (opMap[opKey]) {
      return (
        <div className="aggregation-result">
          <div className="aggregation-value">{opMap[opKey]} {fieldLabel ? `of ${fieldLabel}` : ''} is {`${displayResult}`}.</div>
        </div>
      );
    }
    const op = opKey.charAt(0).toUpperCase() + opKey.slice(1);
    return (
      <div className="aggregation-result">
        <div className="aggregation-op">{op}{fieldLabel ? ` of ${fieldLabel}` : ''}</div>
        <div className="aggregation-value">{`${displayResult}`}</div>
      </div>
    );
  }
  return (
    <div className="aggregation-result">
      <div className="aggregation-op">Result</div>
      <div className="aggregation-value">{`${displayResult}`}</div>
    </div>
  );
}

function Chat({ currentPlan }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Welcome! Ask me a question about your Shopify data with Askeet.' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastResult, setLastResult] = useState(null); // Store latest result for export
  const [lastQuery, setLastQuery] = useState(null); // Store last AI-generated query for save
  const [lastPrompt, setLastPrompt] = useState(''); // Store last user prompt for save
  const [saveSuccess, setSaveSuccess] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const chatScrollRef = useRef(null);
  const [sendDisabled, setSendDisabled] = useState(false);
  const [pagination, setPagination] = useState(null); // Pagination info from backend
  const [pageCursors, setPageCursors] = useState([null]); // Cursors for each page (0: null for first page)
  const [currentPage, setCurrentPage] = useState(1); // 1-based page number
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionError, setSubscriptionError] = useState(null);
  const messagesEndRef = useRef(null);
  const [reauthRequired, setReauthRequired] = useState(false);
  const [reauthModal, setReauthModal] = useState(false);
  const [reauthCountdown, setReauthCountdown] = useState(5);
  const reauthUrlRef = useRef(null);
  const MAX_TRIES = 3;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [dbStructure, setDbStructure] = useState(null);
  const [exportAllLoading, setExportAllLoading] = useState(false);
  const [exportAllError, setExportAllError] = useState(null);
  const [exportAllSuccess, setExportAllSuccess] = useState(null);
  const [exportAllStates, setExportAllStates] = useState({}); // { [index]: { loading, error, success } }

  // Fetch history on mount and after save/delete
  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line
  }, []);

  async function fetchHistory() {
    setHistoryLoading(true);
    setHistoryError(null);
    if (!SHOP_DOMAIN) {
      setHistory([]);
      setHistoryError(requireShopDomain());
      setHistoryLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/history?shop=${INSTALL_ID}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setHistory(data);
      } else {
        setHistory([]);
        setHistoryError('Erreur lors du chargement de l\'historique.');
      }
    } catch (e) {
      setHistory([]);
      setHistoryError('Erreur lors du chargement de l\'historique.');
    }
    setHistoryLoading(false);
  }

  // Fetch Shopify schema on mount
  useEffect(() => {
    async function fetchShopifySchema() {
      if (!SHOP_DOMAIN) return;
      try {
        const res = await fetch(`${API_BASE}/api/shopify-schema?shop=${SHOP_DOMAIN}`);
        const data = await res.json();
        if (data && data.schema) {
          setDbStructure(data.schema);
        }
      } catch (error) {
        console.error('Error fetching Shopify schema:', error);
      }
    }
    fetchShopifySchema();
  }, []);

  // Helper to render a smart, user-friendly cell value
  function renderCell(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
      throw new Error('[FATAL renderCell] Attempted to render object: ' + JSON.stringify(value));
    }
    if (Array.isArray(value)) {
      throw new Error('[FATAL renderCell] Attempted to render array: ' + JSON.stringify(value));
    }
    return String(value);
  }

  // Helper to render results as a table if possible
  function renderResultsTable(data) {
    if (!data || !data.edges) return null;
    const nodes = data.edges.map(e => e.node);
    if (!nodes.length || !nodes[0]) return <div>No results found.</div>;
    const columns = Object.keys(nodes[0] || {});
    return (
      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              {columns.map(col => <th key={col} style={{ border: '1px solid #ddd', padding: 6 }}>{col}</th>)}
            </tr>
          </thead>
          <tbody>
            {nodes.map((row, i) => (
              <tr key={i}>
                {columns.map(col => (
                  <td key={col} style={{ border: '1px solid #ddd', padding: 6, whiteSpace: 'pre-wrap', maxWidth: 220, overflowX: 'auto' }}>{renderCell(row ? row[col] : '')}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Helper to render pagination controls
  function renderPagination() {
    if (!pagination || !pagination.totalCount || pagination.totalCount <= 10) return null;
    const totalPages = Math.ceil(pagination.totalCount / pagination.perPage);
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    return (
      <div style={{ margin: '16px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
        {pageNumbers.map(num => (
          <Button key={num} size="slim" onClick={() => handlePageChange(num)} primary={num === currentPage}>
            {num}
          </Button>
        ))}
      </div>
    );
  }

  // Export result as CSV
  function exportToCSV(data) {
    if (!data || !data.edges || !data.edges.length) return;
    const nodes = data.edges.map(e => e.node);
    if (!nodes.length || !nodes[0]) return;
    const columns = Object.keys(nodes[0] || {});
    const csvRows = [columns.join(',')];
    nodes.forEach(row => {
      csvRows.push(columns.map(col => '"' + String(row[col]).replace(/"/g, '""') + '"').join(','));
    });
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    // Determine language for filename
    const lang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
    let filename = '';
    if (lang.startsWith('fr')) {
      filename = `resultat_requete_page-${currentPage}.csv`;
    } else {
      filename = `result_query_page-${currentPage}.csv`;
    }
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Save last query to backend history
  async function saveQuery() {
    if (!lastQuery || !lastPrompt) return;
    setSaveSuccess(null);
    try {
      const res = await fetch(`${API_BASE}/api/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ install_id: INSTALL_ID, prompt: lastPrompt, query: lastQuery })
      });
      const data = await res.json();
      if (data.id || data.success) {
        setSaveSuccess('Query saved!');
        setSendDisabled(true); // Disable send after successful save
        fetchHistory(); // Refresh history
      } else {
        setSaveSuccess('Error saving query.');
      }
    } catch (e) {
      setSaveSuccess('Error saving query.');
    }
    setTimeout(() => setSaveSuccess(null), 2000);
  }

  // Delete a query from history
  async function deleteHistoryItem(id) {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette requête ?')) return;
    try {
      await fetch(`${API_BASE}/api/history/${id}?shop=${INSTALL_ID}`, {
        method: 'DELETE'
      });
      fetchHistory();
    } catch (e) {
      // ignore
    }
  }

  // Reset sendDisabled on input change or new conversation
  useEffect(() => { setSendDisabled(false); }, [input]);

  // Start a new conversation
  function newConversation() {
    setMessages([{ role: 'assistant', content: 'Welcome! Ask me a question about your Shopify data with Askeet.' }]);
    setInput('');
    setLastPrompt('');
    setLastQuery(null);
    setLastResult(null);
    setError(null);
    setSaveSuccess(null);
    setSendDisabled(false);
    setPagination(null);
    setPageCursors([null]);
    setCurrentPage(1);
  }

  // Universal subscription limit handler
  function handleSubscriptionLimit(response) {
    if (
      response.upgrade_required === true ||
      (typeof response.error === 'string' && /limit|upgrade/i.test(response.error))
    ) {
      setSubscriptionError(response.error || "You have reached your plan's limit.");
      setShowSubscriptionModal(true);
      return true;
    }
    return false;
  }

  // Handle page change for pagination
  async function handlePageChange(pageNum) {
    if (pageNum === currentPage) return;
    setLoading(true);
    setError(null);
    try {
      const cursor = pageCursors[pageNum - 1] || null;
      const shopifyRes = await fetch(`${API_BASE}/api/shopify-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: SHOP_DOMAIN,
          query: lastQuery,
          perPage: 10,
          cursor
        })
      });
      const shopifyData = await shopifyRes.json();
      if (shopifyData.error === 'reauth_required' && shopifyData.reauth_url) {
        console.log('Triggering reauth modal (page change)');
        sessionStorage.setItem('pendingQuery', lastQuery);
        reauthUrlRef.current = shopifyData.reauth_url;
        setReauthCountdown(5);
        setReauthModal(true);
        setLoading(false);
        return;
      }
      if (handleSubscriptionLimit(shopifyData)) return;
      const mainKey = shopifyData.data ? Object.keys(shopifyData.data)[0] : null;
      const resultData = mainKey ? shopifyData.data[mainKey] : null;
      if (!resultData || !resultData.edges || resultData.edges.length === 0) {
        setLastResult(resultData);
        setPagination(shopifyData.pagination || null);
        setMessages(msgs => [
          ...msgs.filter(m => !m.data),
          ...(shopifyData.warning ? [{ role: 'warning', content: shopifyData.warning }] : []),
          {
            role: 'assistant',
            content: 'No data found for your request.',
            data: resultData
          }
        ]);
        setLoading(false);
        return;
      }
      setLastResult(resultData);
      setPagination(shopifyData.pagination || null);
      setCurrentPage(pageNum);
      setPageCursors(prev => {
        const next = [...prev];
        next[pageNum] = shopifyData.pagination?.endCursor || null;
        return next;
      });
      setMessages(msgs => [
        ...msgs.filter(m => !m.data),
        ...(shopifyData.warning ? [{ role: 'warning', content: shopifyData.warning }] : []),
        {
          role: 'assistant',
          content: resultData && resultData.edges && resultData.edges.length > 0 ? 'Here are the results:' : 'No results found.',
          data: resultData
        }
      ]);
    } catch (err) {
      setError(getFriendlyShopifyError(err.message));
      setMessages(msgs => [...msgs, { role: 'error', content: getFriendlyShopifyError(err.message) }]);
    } finally {
      setLoading(false);
    }
  }

  // Helper to get previous user and AI messages for context
  function getPreviousContext() {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user');
    const lastAIMsg = [...messages].reverse().find(m => m.role === 'assistant');
    return {
      previous_user_query: lastUserMsg ? lastUserMsg.content : '',
      previous_ai_response: lastAIMsg ? lastAIMsg.content : ''
    };
  }

  // PATCH: Helper to render aggregation result
  function renderAggregationResult(result, aggregation) {
    console.log('[renderAggregationResult] result:', result, 'aggregation:', aggregation, 'typeof result:', typeof result, 'isArray:', Array.isArray(result));
    // --- PATCH: Show correct operation label ---
    if (aggregation && aggregation.operation) {
      const opKey = aggregation.operation.toLowerCase();
      const op = OP_LABELS[opKey] || (opKey.charAt(0).toUpperCase() + opKey.slice(1));
      const fieldLabel = aggregation.field ? prettifyFieldName(aggregation.field) : '';
      // User-friendly phrasing for count
      if (opKey === 'count') {
        // Try to infer the entity type from the field or context
        let entity = 'items';
        if (/order/i.test(fieldLabel)) entity = 'orders';
        else if (/customer/i.test(fieldLabel)) entity = 'customers';
        else if (/product/i.test(fieldLabel)) entity = 'products';
        else if (/id/i.test(fieldLabel)) entity = 'items';
        return (
          <div className="aggregation-result">
            <div className="aggregation-value">You have {result} {entity}.</div>
          </div>
        );
      }
      // User-friendly phrasing for sum, average, min, max
      const opMap = {
        sum: 'The total',
        average: 'The average',
        min: 'The minimum',
        max: 'The maximum'
      };
      if (opMap[opKey]) {
        return (
          <div className="aggregation-result">
            <div className="aggregation-value">{opMap[opKey]} {fieldLabel ? `of ${fieldLabel}` : ''} is {result}.</div>
          </div>
        );
      }
      // Fallback
      return (
        <div className="aggregation-result">
          <div className="aggregation-op">{op}{fieldLabel ? ` of ${fieldLabel}` : ''}</div>
          <div className="aggregation-value">{result}</div>
        </div>
      );
    }
    // Fallback if aggregation is missing
    return (
      <div className="aggregation-result">
        <div className="aggregation-op">Result</div>
        <div className="aggregation-value">{result}</div>
      </div>
    );
  }

  // Handle send
  const handleSend = async () => {
    if (!input.trim()) return;
    setMessages(msgs => [...msgs, { role: 'user', content: input }]);
    setLastPrompt(input);
    setInput('');
    setLoading(true);
    setError(null);

    let tries = 0;
    let lastAiQuery = input;
    let lastError = null;
    const { previous_user_query, previous_ai_response } = getPreviousContext();

    while (tries < MAX_TRIES) {
      tries += 1;
      // 1. Call AI endpoint
      const aiRes = await fetch(`${API_BASE}/api/ask-ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: lastAiQuery,
          previous_user_query,
          previous_ai_response,
          install_id: INSTALL_ID,
          platform: 'shopify',
          db_structure: dbStructure || ''
        })
      });
      const aiData = await aiRes.json();
      console.log('[AI RESPONSE]', aiData);
      if (aiData && aiData.upgrade_required === true) {
        setLoading(false);
        setSubscriptionError(aiData.error || "You have reached your plan's limit.");
        setShowSubscriptionModal(true);
        return;
      }
      if (!aiData) {
        setError('Unexpected error: No response from server.');
        setLoading(false);
        return;
      }
      // Robustly extract query and aggregation from AI response
      let aiQuery, aiAggregation;
      if (typeof aiData === 'string') {
        // Try to extract JSON block
        const match = aiData.match(/{[\s\S]*}/);
        if (match) {
          try {
            const parsed = JSON.parse(match[0]);
            aiQuery = parsed.query || aiData;
            aiAggregation = parsed.aggregation || null;
          } catch {
            aiQuery = aiData;
            aiAggregation = null;
          }
        } else {
          aiQuery = aiData;
          aiAggregation = null;
        }
      } else {
        aiQuery = aiData.sql_query || aiData.query;
        aiAggregation = aiData.aggregation || null;
      }
      if (!aiRes.ok || !aiQuery) {
        lastError = aiData.error || 'Erreur AI';
        break;
      }
      setLastQuery(aiQuery);

      // 2. Call Shopify API endpoint (first page)
      const shopifyRes = await fetch(`${API_BASE}/api/shopify-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shop: SHOP_DOMAIN,
          query: aiQuery,
          aggregation: aiAggregation, // PATCH: send aggregation if present
          perPage: 10,
          cursor: null
        })
      });
      const shopifyData = await shopifyRes.json();
      console.log('[SHOPIFY DATA RECEIVED]', shopifyData);
      // PATCH: Show warning if present
      if (shopifyData.warning) {
        setMessages(msgs => [
          ...msgs,
          { role: 'warning', content: shopifyData.warning }
        ]);
      }
      // PATCH: If aggregation_result is present, show it as a message and skip table
      if (shopifyData && shopifyData.aggregation_result !== undefined) {
        setMessages(msgs => [
          ...msgs,
          {
            role: 'assistant',
            content: '',
            aggregation_result: shopifyData.aggregation_result,
            aggregation_type: shopifyData.aggregation_type
          }
        ]);
        setLoading(false);
        return;
      }
      if (shopifyData && shopifyData.error === 'reauth_required' && shopifyData.reauth_url) {
        sessionStorage.setItem('pendingQuery', aiQuery);
        reauthUrlRef.current = shopifyData.reauth_url;
        setReauthCountdown(5);
        setReauthModal(true);
        setLoading(false);
        return;
      }
      if (shopifyData && shopifyData.upgrade_required === true) {
        setLoading(false);
        setSubscriptionError(shopifyData.error || "You have reached your plan's limit.");
        setShowSubscriptionModal(true);
        return;
      }
      if (!shopifyData) {
        setError('Unexpected error: No response from server.');
        setLoading(false);
        return;
      }
      // --- POST-QUERY CONTROL: Handle no results gracefully ---
      const mainKey = shopifyData.data ? Object.keys(shopifyData.data)[0] : null;
      const resultData = mainKey ? shopifyData.data[mainKey] : null;
      if (!shopifyData.error && (!resultData || !resultData.edges || resultData.edges.length === 0)) {
        setLastResult(resultData);
        setPagination(shopifyData.pagination || null);
        setCurrentPage(1);
        setPageCursors([null, shopifyData.pagination?.endCursor || null]);
        setMessages(msgs => [
          ...msgs,
          { role: 'assistant', content: 'No data found for your request.', data: resultData }
        ]);
        setLoading(false);
        return;
      }
      if (shopifyData.error) {
        lastError = shopifyData.error;
        lastAiQuery = `Fix this query for Shopify. The error was: ${shopifyData.error}. Original user request: ${input}`;
        continue;
      }
      // Success!
      setLastResult(resultData);
      setPagination(shopifyData.pagination || null);
      setCurrentPage(1);
      setPageCursors([null, shopifyData.pagination?.endCursor || null]);
      setMessages(msgs => [
        ...msgs,
        { role: 'assistant', content: 'Here are the results:', data: resultData }
      ]);
      setLoading(false);
      return;
    }

    // If we get here, all tries failed
    setError(getFriendlyShopifyError(lastError));
    setMessages(msgs => [
      ...msgs,
      { role: 'assistant', content: "Sorry, I couldn't understand your request or found no results. Please try rephrasing your question." }
    ]);
    setLoading(false);
  };

  // Add handler for example queries
  const handleExampleClick = (query) => {
    setInput('');
    setTimeout(() => {
      setInput(query);
      setTimeout(() => handleSend(), 100);
    }, 10);
  };

  // Render function for each message (fix result rendering)
  const renderMessage = (message, index) => {
    console.log('[renderMessage] message:', message, 'index:', index);
    let element = null;
    if (message.role === 'user') {
      // Show Save button next to user query if it is the lastPrompt and can be saved
      const canSave = lastPrompt === message.content && lastQuery && !sendDisabled;
      element = (
        <div key={index} className="message user" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="message-content">{message.content}</div>
          {canSave && (
            <Button onClick={saveQuery} size="slim" primary disabled={sendDisabled} style={{ marginLeft: 8 }}>
              Save
            </Button>
          )}
        </div>
      );
    } else if (message.role === 'assistant') {
      // PATCH: Only show aggregation result if the user intent is aggregation
      const isAggregationIntent = /how many|number of|count of|total|sum|average|mean|max|min|what is the|what's the|show me the|give me the|how much|how high|how low|lowest|highest|most|least|minimum|maximum/i.test(lastPrompt || '');
      if (message.aggregation_result !== undefined) {
        console.log('[renderMessage] Rendering aggregation_result:', message.aggregation_result, 'aggregation_type:', message.aggregation_type);
        element = (
          <div key={index} className="message assistant">
            {renderAggregationResult(message.aggregation_result, message.aggregation_type)}
          </div>
        );
      } else if (message.data && message.data.edges && message.data.edges.length > 0) {
        // Find the original query for this result (if available)
        const originalQuery = message.query || lastQuery;
        const exportState = exportAllStates[index] || {};
        element = (
          <div key={index} className="message assistant">
            <div className="results-table-fullwidth">{renderResultsTable(message.data)}</div>
            <div style={{ marginTop: 8, display: 'flex', gap: 10, alignItems: 'center' }}>
              <Button onClick={() => exportToCSV(message.data)} size="slim">Export Current Page</Button>
              {/* <Button onClick={() => exportAllResultsForQuery(originalQuery, index)} size="slim" loading={exportState.loading} disabled={exportState.loading}>
                Export All Results
              </Button> */}
              {exportState.loading && <span style={{ marginLeft: 8 }}><Spinner size="small" /></span>}
              {exportState.error && <span style={{ color: 'red', marginLeft: 8 }}>{exportState.error}</span>}
              {exportState.success && <span style={{ color: 'green', marginLeft: 8 }}>{exportState.success}</span>}
            </div>
          </div>
        );
      } else {
        element = (
          <div key={index} className="message assistant">
            <div className="message-content">{message.content}</div>
          </div>
        );
      }
    } else if (message.role === 'warning') {
      element = (
        <div key={index} className="message warning-message">{message.content}</div>
      );
    } else if (message.role === 'error') {
      element = (
        <div key={index} className="message error-message">{message.content}</div>
      );
    }
    // Defensive: always return a single React element
    if (Array.isArray(element)) {
      console.log('[renderMessage] element is array, wrapping in Fragment:', element);
      return <React.Fragment key={index}>{element}</React.Fragment>;
    }
    if (element === undefined || element === null) return null;
    return element;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Always show the chat section, even if messages is empty
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{ role: 'assistant', content: 'Welcome! Ask me a question about your Shopify data with Askeet.' }]);
    }
    // eslint-disable-next-line
  }, [messages]);

  // Countdown effect for reauth modal
  useEffect(() => {
    if (!reauthModal) return;
    if (reauthCountdown <= 0) {
      setReauthModal(false);
      window.top.location.href = reauthUrlRef.current;
      return;
    }
    const timer = setTimeout(() => setReauthCountdown(reauthCountdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [reauthModal, reauthCountdown]);

  // Rerun a saved query from history (with limit check)
  async function rerunHistoryItem(item) {
    setLoading(true);
    setError(null);
    setMessages(msgs => [
      ...msgs,
      { role: 'user', content: item.prompt }
    ]);
    try {
      const res = await fetch(`${API_BASE}/api/execute-saved-query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: item.id, shop: INSTALL_ID })
      });
      let data = null;
      try {
        data = await res.json();
      } catch (jsonErr) {
        setError('Unexpected error: Could not parse server response.');
        setLoading(false);
        return;
      }
      if (!data) {
        setError('Unexpected error: No response from server.');
        setLoading(false);
        return;
      }
      // Handle subscription blocking for saved queries
      if (data.upgrade_required === true) {
        setSubscriptionError(data.error || "You have reached your plan's limit.");
        setShowSubscriptionModal(true);
        setLoading(false);
        return;
      }
      if (data.error) {
        setError(data.error);
        setMessages(msgs => [
          ...msgs,
          { role: 'assistant', content: data.error }
        ]);
      } else if (data.data) {
        // Standard Shopify GraphQL response
        const mainKey = Object.keys(data.data)[0];
        const resultData = data.data[mainKey];
        setLastResult(resultData);
        setMessages(msgs => [
          ...msgs,
          { role: 'assistant', content: 'Here are the results:', data: resultData }
        ]);
      } else {
        setError('No data returned.');
        setMessages(msgs => [
          ...msgs,
          { role: 'assistant', content: 'No data returned.' }
        ]);
      }
    } catch (e) {
      setError('Erreur lors de l\'exécution de la requête sauvegardée.');
      setMessages(msgs => [
        ...msgs,
        { role: 'assistant', content: 'Erreur lors de l\'exécution de la requête sauvegardée.' }
      ]);
    }
    setLoading(false);
  }

  const shopDomain = getShopDomain() || SHOP_DOMAIN;
  const appSlug = 'askeet';
  const adminAppUrl = shopDomain ? `https://admin.shopify.com/store/${shopDomain.replace('.myshopify.com','')}/apps/${appSlug}` : window.location.origin;

  // Export all data as CSV (calls backend)
  async function exportAllData() {
    setExportAllLoading(true);
    setExportAllError(null);
    setExportAllSuccess(null);
    if (!SHOP_DOMAIN) {
      setExportAllError(requireShopDomain());
      setExportAllLoading(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/export-all?shop=${SHOP_DOMAIN}&type=customers`);
      if (res.status === 400) {
        const data = await res.json();
        if (data.error === 'too_many_records') {
          setExportAllError(`Export too large: ${data.count} records. Please filter your data or contact support.`);
        } else {
          setExportAllError(data.error || 'Export failed.');
        }
        setExportAllLoading(false);
        return;
      }
      if (!res.ok) throw new Error('Export failed.');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Determine language for filename
      const lang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
      let filename = '';
      if (lang.startsWith('fr')) {
        filename = `resultat_requete_all.csv`;
      } else {
        filename = `result_query_all.csv`;
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExportAllSuccess('Export complete!');
    } catch (err) {
      setExportAllError(err.message || 'Export failed.');
    }
    setExportAllLoading(false);
    setTimeout(() => setExportAllError(null), 4000);
    setTimeout(() => setExportAllSuccess(null), 4000);
  }

  // Export all results for a specific query (per result block)
  async function exportAllResultsForQuery(query, index) {
    setExportAllStates(prev => ({ ...prev, [index]: { loading: true, error: null, success: null } }));
    if (!SHOP_DOMAIN) {
      setExportAllStates(prev => ({ ...prev, [index]: { loading: false, error: requireShopDomain(), success: null } }));
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/export-query-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shop: SHOP_DOMAIN, query })
      });
      if (res.status === 400) {
        const data = await res.json();
        if (data.error === 'too_many_records') {
          setExportAllStates(prev => ({ ...prev, [index]: { loading: false, error: `Export too large: ${data.count} records. Please filter your data or contact support.`, success: null } }));
        } else {
          setExportAllStates(prev => ({ ...prev, [index]: { loading: false, error: data.error || 'Export failed.', success: null } }));
        }
        return;
      }
      if (!res.ok) throw new Error('Export failed.');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Determine language for filename
      const lang = (navigator.language || navigator.userLanguage || 'en').toLowerCase();
      let filename = '';
      if (lang.startsWith('fr')) {
        filename = `resultat_requete_all_${index + 1}.csv`;
      } else {
        filename = `result_query_all_${index + 1}.csv`;
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setExportAllStates(prev => ({ ...prev, [index]: { loading: false, error: null, success: 'Export complete!' } }));
    } catch (err) {
      setExportAllStates(prev => ({ ...prev, [index]: { loading: false, error: err.message || 'Export failed.', success: null } }));
    }
    setTimeout(() => setExportAllStates(prev => ({ ...prev, [index]: { ...prev[index], error: null, success: null } })), 4000);
  }

  // Defensive: ensure messages.map(renderMessage) returns only valid React elements
  let renderedMessages = messages.map(renderMessage);
  const hasNestedArray = renderedMessages.some(Array.isArray);
  if (hasNestedArray) {
    console.warn('[Chat] Nested array detected in renderedMessages, flattening:', renderedMessages);
    renderedMessages = renderedMessages.flat();
  }
  const invalidElements = renderedMessages.filter(el => !React.isValidElement(el) && el !== null);
  if (invalidElements.length > 0) {
    console.error('[Chat] Invalid React elements detected in renderedMessages:', invalidElements);
  }

  // Layout: sidebar + main chat
  return (
    <div className="chat-outer-container">
      <div className="askeet-panel">
        {/* Sidebar */}
        <div className="chat-sidebar" style={{ position: 'relative', minHeight: '80vh' }}>
          <Button fullWidth primary onClick={newConversation} style={{ marginBottom: 24 }}>
            New conversation
          </Button>
          <hr style={{ border: 'none', borderTop: '1px solid #eee', margin: '0 0 16px 0' }} />
          <div style={{ fontWeight: 600, marginBottom: 8 }}>History</div>
          {historyLoading ? (
            <Spinner accessibilityLabel="Chargement" size="small" />
          ) : historyError ? (
            <div style={{ color: 'red' }}>{historyError}</div>
          ) : Array.isArray(history) && history.length === 0 ? (
            <div>Aucune requête sauvegardée.</div>
          ) : Array.isArray(history) ? (
            <BlockStack gap="100">
              {history.map(item => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #eee', padding: '8px 0', cursor: 'pointer' }}>
                  <div style={{ flex: 1, position: 'relative' }}>
                    <HistoryPromptTruncated prompt={item.prompt} onClick={() => rerunHistoryItem(item)} />
                  </div>
                  <Button size="slim" onClick={() => deleteHistoryItem(item.id)} plain destructive>🗑️</Button>
                </div>
              ))}
            </BlockStack>
          ) : (
            <div style={{ color: 'red' }}>Erreur lors du chargement de l'historique.</div>
          )}
        </div>
        {/* Main chat area */}
        <div className="chat-main-area">
          <div className="chat-card" style={{ padding: '32px 24px 24px 24px' }}>
            <div ref={chatScrollRef} className="messages-container">
              <BlockStack gap="200">
                {messages.length === 0 && (
                  <div style={{ color: 'gray', margin: 16 }}>No messages. Please reload the page.</div>
                )}
                {renderedMessages}
                <div ref={messagesEndRef} />
                {saveSuccess && <div style={{ color: saveSuccess.includes('Error') ? 'red' : 'green', marginTop: 8 }}>{saveSuccess}</div>}
                {loading && <Spinner accessibilityLabel="Loading" size="small" />}
                {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
              </BlockStack>
            </div>
            <InlineStack gap="200" className="input-bar" style={{ marginTop: 32, marginBottom: 24 }}>
              <div style={{ flex: 1, minWidth: 350, maxWidth: 600 }}>
                <TextField
                  value={input}
                  onChange={setInput}
                  onEnterPressed={handleSend}
                  placeholder="Ask your question about Shopify..."
                  autoComplete="off"
                  multiline={{ min: 3, max: 3 }}
                  style={{ height: 72, overflowY: 'auto' }}
                />
              </div>
              <Button primary onClick={handleSend} disabled={loading || !input.trim()}>
                Send
              </Button>
            </InlineStack>
            {/* Example queries at the bottom, styled as attractive cards/buttons */}
            <div className="example-queries-below" style={{ marginTop: 32, marginBottom: 8, display: 'flex', gap: 20, justifyContent: 'center' }}>
              {EXAMPLE_QUERIES.map((q, i) => (
                <div
                  key={i}
                  className="example-query-card"
                  style={{
                    background: 'rgb(48, 48, 48)',
                    borderRadius: 12,
                    padding: '16px 24px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                    cursor: 'pointer',
                    fontWeight: 500,
                    // fontSize: 16,
                    color: 'rgb(255, 255, 255)',
                    transition: 'box-shadow 0.2s',
                    border: '2px solid #e0e0e0',
                    minWidth: 180,
                    textAlign: 'center',
                    fontSize: '12px',
                  }}
                  onClick={() => handleExampleClick(q.replaceAll('\"', ''))}
                  onMouseOver={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.10)'}
                  onMouseOut={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'}
                >
                  <span role="img" aria-label="lightbulb" style={{ marginRight: 8 }}><p style={{fontSize: 16, margin: "0 0 10px 0"}}>{ i == 0 ? 'Sales' : (i == 1 ? 'Marketing' : 'Finance')}</p></span>
                  {q}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Blocking modal for subscription */}
      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        currentPlan={currentPlan}
        error={subscriptionError}
        onUpgradeSuccess={() => setShowSuccessModal(true)}
      />
      {showSubscriptionModal && <div className="modal-blocker" />}
      {/* Congratulations Modal */}
      {showSuccessModal && (
        <div className="subscription-modal-overlay" style={{ zIndex: 2000 }}>
          <div className="success-modal" style={{
            background: 'white',
            borderRadius: 16,
            maxWidth: 420,
            minWidth: 320,
            minHeight: 340,
            padding: '48px 32px 40px 32px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            textAlign: 'center',
          }}>
            <button className="close-button" style={{ position: 'absolute', top: 18, right: 18 }} onClick={() => setShowSuccessModal(false)}>×</button>
            <div style={{ fontSize: 64, marginBottom: 18 }}>🎉</div>
            <h2 style={{ marginBottom: 16, color: '#005a9e', fontWeight: 700, fontSize: 28 }}>Congratulations!</h2>
            <div style={{ fontSize: 20, marginBottom: 24, color: '#333' }}>
              Your subscription has been <b>upgraded successfully</b> to <span style={{ color: '#0070f3' }}>Unlimited</span>!<br />Enjoy all premium features.
            </div>
            <Button primary size="large" onClick={() => setShowSuccessModal(false)}>
              Close
            </Button>
          </div>
        </div>
      )}
      {reauthModal && (
        <>
          <div className="modal-blocker" />
          <div className="reauth-modal-overlay">
            <div className="reauth-modal">
              <h2>Shopify Re-Authorization Needed</h2>
              <p>Your Shopify connection has expired or is missing.<br />
                You will be redirected to reconnect in <b>{reauthCountdown}</b> seconds…
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function HistoryPromptTruncated({ prompt, onClick }) {
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <>
      <span
        className="history-prompt-truncated"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={onClick}
        style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200, display: 'inline-block', cursor: 'pointer', color: '#1a1a1a' }}
      >
        {prompt.length > 16 ? prompt.slice(0, 16) + '...' : prompt}
      </span>
      {showTooltip && (
        <div className="history-tooltip-z" style={{ position: 'absolute', left: 0, top: 28, zIndex: 100, background: '#fff', color: '#222', border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', padding: '10px 16px', minWidth: 180, maxWidth: 320, fontSize: 15, fontWeight: 400, whiteSpace: 'pre-line' }}>
          {prompt}
        </div>
      )}
    </>
  );
}

export default Chat; 